import jsPDF from "jspdf";
import QRCode from "qrcode";
import logoImobi from "../../assets/logo_minimal.png";
import type { Asset, Company, LabelElement, LabelTemplate } from "../types/imobi";

export type LabelRenderOptions = {
  asset: Asset;
  company: Company;
  template: LabelTemplate;
  issuedAt: Date;
};

function mmToPx(mm: number) {
  return Math.round(mm * 8);
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function drawBarcode(context: CanvasRenderingContext2D, value: string, x: number, y: number, width: number, height: number) {
  const digits = value.replace(/\D/g, "") || value;
  let cursor = x;
  const barWidth = Math.max(1, Math.floor(width / Math.max(24, digits.length * 4)));

  for (let index = 0; index < Math.max(24, digits.length * 4); index += 1) {
    const code = digits.charCodeAt(index % digits.length) + index;
    const isBar = code % 3 !== 0;
    const currentWidth = barWidth * (code % 2 === 0 ? 1 : 2);

    if (isBar) {
      context.fillRect(cursor, y, currentWidth, height);
    }

    cursor += currentWidth + barWidth;
    if (cursor > x + width) {
      break;
    }
  }
}

function resolveCodeValue(asset: Asset, codeSource: LabelTemplate["codeSource"]) {
  if (codeSource === "patrimonyNumber") {
    return asset.patrimonyNumber;
  }

  if (codeSource === "internalCode") {
    return asset.internalCode || asset.patrimonyNumber;
  }

  return `${window.location.origin}/assets/${asset.id}?companyId=${asset.companyId}`;
}

function labelFieldValue(field: LabelElement["field"], asset: Asset, company: Company, issuedAt: Date) {
  if (!field) {
    return "";
  }

  const values = {
    patrimonyNumber: asset.patrimonyNumber,
    internalCode: asset.internalCode,
    shortDescription: asset.shortDescription || asset.description,
    companyName: company.tradeName,
    branchName: asset.branchId,
    issuedAt: issuedAt.toLocaleString("pt-BR"),
    physicalLocation: asset.physicalLocation,
    responsible: asset.responsible ?? "",
    createdAt: asset.createdAt ? new Date(asset.createdAt).toLocaleString("pt-BR") : ""
  };

  return values[field] ?? "";
}

async function renderCodeElement(
  context: CanvasRenderingContext2D,
  element: LabelElement,
  asset: Asset,
  template: LabelTemplate,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const parts: Array<"qr" | "barcode" | "number"> = [];
  if (element.showQrCode) parts.push("qr");
  if (element.showBarcode) parts.push("barcode");
  if (element.showNumber) parts.push("number");
  if (!parts.length) {
    return;
  }

  const codeSource = element.codeSource ?? template.codeSource ?? "assetUrl";
  const codeValue = resolveCodeValue(asset, codeSource);
  const fontSize = element.fontSize ?? 9;
  const numberHeight = parts.includes("number") ? Math.max(12, fontSize + 6) : 0;
  const barcodeHeight = parts.includes("barcode") ? Math.max(14, Math.round(h * 0.22)) : 0;
  const qrHeight = parts.includes("qr") ? Math.max(0, h - numberHeight - barcodeHeight) : 0;

  let cursorY = y;

  if (parts.includes("qr")) {
    const qrSize = Math.max(1, Math.min(w, qrHeight));
    const qrX = x + (w - qrSize) / 2;
    const qrDataUrl = await QRCode.toDataURL(codeValue, { margin: 0, width: qrSize });
    const qrImage = await loadImage(qrDataUrl);
    if (qrImage) {
      context.drawImage(qrImage, qrX, cursorY, qrSize, qrSize);
    }
    cursorY += qrHeight;
  }

  if (parts.includes("barcode")) {
    context.fillStyle = "#111827";
    const barcodeValue = resolveCodeValue(asset, codeSource === "assetUrl" ? "patrimonyNumber" : codeSource);
    drawBarcode(context, barcodeValue, x, cursorY, w, barcodeHeight);
    cursorY += barcodeHeight;
  }

  if (parts.includes("number")) {
    context.fillStyle = element.color ?? "#111827";
    context.font = `${element.fontWeight ?? "600"} ${fontSize}px ${template.fontFamily ?? "Arial"}`;
    context.textAlign = "center";
    const numberValue = codeSource === "assetUrl" ? asset.patrimonyNumber : codeValue;
    context.fillText(numberValue, x + w / 2, cursorY + numberHeight * 0.7, w);
    context.textAlign = "left";
  }
}

function renderFieldElement(
  context: CanvasRenderingContext2D,
  element: LabelElement,
  asset: Asset,
  company: Company,
  issuedAt: Date,
  template: LabelTemplate,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const value = labelFieldValue(element.field, asset, company, issuedAt);
  if (!value) {
    return;
  }

  const finalText = value.length > 60 ? `${value.slice(0, 57)}...` : value;
  const align = element.textAlign ?? "left";
  const textX = align === "left" ? x : align === "right" ? x + w : x + w / 2;

  context.fillStyle = element.color ?? "#111827";
  context.font = `${element.fontWeight ?? "400"} ${element.fontSize ?? 8}px ${template.fontFamily ?? "Arial"}`;
  context.textAlign = align;
  context.fillText(finalText, textX, y + h * 0.75, w);
  context.textAlign = "left";
}

export async function renderAssetLabel(options: LabelRenderOptions): Promise<string> {
  const { asset, company, template, issuedAt } = options;
  const width = mmToPx(template.widthMm);
  const height = mmToPx(template.heightMm);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas nao disponivel para gerar etiqueta.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "#111827";
  context.lineWidth = 2;
  context.strokeRect(1, 1, width - 2, height - 2);

  const elements = template.elements ?? [];
  for (const element of elements) {
    const x = mmToPx(element.xMm);
    const y = mmToPx(element.yMm);
    const w = mmToPx(element.widthMm);
    const h = mmToPx(element.heightMm);

    if (element.type === "logoImobi" && template.showImobiLogo) {
      const image = await loadImage(logoImobi);
      if (image) {
        context.drawImage(image, x, y, w, h);
      }
    } else if (element.type === "logoCompany" && template.showCompanyLogo && company.logoUrl) {
      const image = await loadImage(company.logoUrl);
      if (image) {
        context.drawImage(image, x, y, w, h);
      }
    } else if (element.type === "code") {
      await renderCodeElement(context, element, asset, template, x, y, w, h);
    } else if (element.type === "field") {
      renderFieldElement(context, element, asset, company, issuedAt, template, x, y, w, h);
    }
  }

  return canvas.toDataURL("image/png");
}

export async function downloadAssetLabelPng(options: LabelRenderOptions): Promise<void> {
  const dataUrl = await renderAssetLabel(options);
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = `imobi-etiqueta-${options.asset.patrimonyNumber}.png`;
  anchor.click();
}

export async function downloadAssetLabelPdf(options: LabelRenderOptions): Promise<void> {
  const dataUrl = await renderAssetLabel(options);
  const doc = new jsPDF({
    orientation: options.template.widthMm >= options.template.heightMm ? "landscape" : "portrait",
    unit: "mm",
    format: [options.template.widthMm, options.template.heightMm]
  });
  doc.addImage(dataUrl, "PNG", 0, 0, options.template.widthMm, options.template.heightMm);
  doc.save(`imobi-etiqueta-${options.asset.patrimonyNumber}.pdf`);
}

export async function printAssetLabel(options: LabelRenderOptions): Promise<void> {
  const dataUrl = await renderAssetLabel(options);
  const printWindow = window.open("", "_blank", "width=420,height=320");
  if (!printWindow) {
    throw new Error("O navegador bloqueou a janela de impressao.");
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>Etiqueta ${options.asset.patrimonyNumber}</title>
        <style>
          @page { size: ${options.template.widthMm}mm ${options.template.heightMm}mm; margin: 0; }
          body { margin: 0; display: grid; place-items: center; min-height: 100vh; }
          img { width: ${options.template.widthMm}mm; height: ${options.template.heightMm}mm; }
        </style>
      </head>
      <body><img src="${dataUrl}" /></body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}
